/**
 * `/password-reset` and `/password-reset/complete`
 *
 * The request step ALWAYS reports the same thing, whether or not the address has an account. That
 * is the server's contract and the UI must preserve it: rendering "no account found" here would
 * hand back the enumeration oracle the server carefully avoided.
 */

import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';

import { AuthCard, AuthLayout } from '../../components/composites/AuthLayout';
import { Button, Field, Notice } from '../../components/primitives';
import { ApiError, completePasswordReset, requestPasswordReset } from './api';

export function RequestPasswordResetScreen(): React.JSX.Element {
  const { t } = useTranslation('common');
  const [email, setEmail] = useState('');

  const mutation = useMutation({ mutationFn: () => requestPasswordReset(email) });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    mutation.mutate();
  };

  return (
    <AuthLayout
      eyebrow="PIUS SELECTIONS"
      heading={t('auth.reset.title')}
      blurb={t('auth.reset.lede')}
    >
      {mutation.isSuccess ? (
        <>
          {/* Identical for known and unknown addresses — deliberately. */}
          <Notice tone="success">{mutation.data.message}</Notice>
          <p style={{ fontSize: 13, lineHeight: 1.8 }}>
            <Link to="/login">{t('auth.reset.back_signin')}</Link>
          </p>
        </>
      ) : (
        <>
          <p style={{ fontSize: 13.5, lineHeight: 1.75, marginTop: 0, marginBottom: 20 }}>
            {t('auth.reset.lede')}
          </p>

          <form onSubmit={submit} noValidate>
            <AuthCard>
              <Field
                label={t('auth.reset.email')}
                type="email"
                name="email"
                placeholder="name@parish.org"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />

              <Button type="submit" variant="primary" disabled={mutation.isPending}>
                {mutation.isPending ? t('auth.reset.submitting') : t('auth.reset.submit')}
              </Button>
            </AuthCard>
          </form>

          <p style={{ fontSize: 13, marginTop: 22 }}>
            <Link to="/login">{t('auth.reset.back_signin')}</Link>
          </p>
        </>
      )}
    </AuthLayout>
  );
}

/**
 * The screen the emailed link lands on.
 *
 * Completing a reset revokes every session, including this browser's — that is the point, since a
 * reset is what someone does when they believe an intruder has access. The copy says so, so being
 * signed out afterwards reads as confirmation rather than as a bug.
 */
export function CompletePasswordResetScreen(): React.JSX.Element {
  const { t } = useTranslation('common');
  const search = useSearch({ from: '/password-reset/complete' });
  const [password, setPassword] = useState('');
  const navigate = useNavigate();

  const mutation = useMutation({
    mutationFn: () => completePasswordReset(search.token ?? '', password),
    onSuccess: () => navigate({ to: '/login' }),
  });

  const error = mutation.error instanceof ApiError ? mutation.error : undefined;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    mutation.mutate();
  };

  if (!search.token) {
    return (
      <AuthLayout eyebrow="PIUS SELECTIONS" heading={t('auth.reset.title')}>
        <Notice>That link is missing its token. Request a new reset link.</Notice>
        <p style={{ fontSize: 13 }}>
          <Link to="/password-reset">{t('auth.reset.title')}</Link>
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout eyebrow="PIUS SELECTIONS" heading={t('auth.reset.title')}>
      {error ? (
        <>
          <Notice referenceId={error.correlationId}>{error.message}</Notice>
          <p style={{ fontSize: 13, marginBottom: 18 }}>
            <Link to="/password-reset">{t('auth.reset.title')}</Link>
          </p>
        </>
      ) : null}

      <form onSubmit={submit} noValidate>
        <AuthCard>
          <Field
            label="New password"
            type="password"
            name="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            hint="Setting a new password signs you out everywhere, on every device."
            autoComplete="new-password"
            required
          />

          <Button type="submit" variant="primary" disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : 'Set new password'}
          </Button>
        </AuthCard>
      </form>
    </AuthLayout>
  );
}
